import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2, Plus, Sparkles, ExternalLink, Tag, Box, Swords, Search,
  MoreHorizontal, Archive, ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { AutofillModal } from "@/components/AutofillModal";

const WRITE_ROLES = ["owner", "admin", "researcher"];

export default function Brands() {
  const { t } = useTranslation(["brands", "common", "errors"]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [openAutofill, setOpenAutofill] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/brands?include_archived=${showArchived ? "true" : "false"}`);
      setBrands(data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [t, showArchived]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter(
      (b) =>
        b.name?.toLowerCase().includes(q) ||
        b.industry?.toLowerCase().includes(q) ||
        b.url?.toLowerCase().includes(q)
    );
  }, [brands, query]);

  const onAutofillSuccess = ({ draft, source_url }) => {
    setOpenAutofill(false);
    navigate("/brands/new/review", { state: { draft, source_url } });
  };

  const onArchive = async (b) => {
    try {
      const { data } = await api.post(`/brands/${b.id}/archive`);
      toast.success(t("brands:detail.archived"));
      setBrands((prev) => prev.map((row) => row.id === b.id ? { ...row, archived_at: data.archived_at } : row));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    }
  };

  const onUnarchive = async (b) => {
    try {
      await api.post(`/brands/${b.id}/unarchive`);
      toast.success(t("brands:detail.unarchived"));
      setBrands((prev) => prev.map((row) => row.id === b.id ? { ...row, archived_at: null } : row));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    }
  };

  return (
    <div className="space-y-8" data-testid="brands-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <Tag className="mr-1 inline h-3 w-3" strokeWidth={2} />
            EvidenceLab
          </span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
            {t("brands:list.title")}
          </h1>
          <p className="max-w-xl text-sm text-slate-500">{t("brands:list.subtitle")}</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpenCreate(true)}
              data-testid="brands-new-manual"
              className="h-10 border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
              <Plus className="mr-1 h-4 w-4" strokeWidth={2} /> {t("brands:list.newBrand")}
            </Button>
            <Button onClick={() => setOpenAutofill(true)}
              data-testid="brands-new-from-url"
              className="h-10 bg-slate-900 text-white hover:bg-slate-800">
              <Sparkles className="mr-1 h-4 w-4" strokeWidth={2} /> {t("brands:list.newFromUrl")}
            </Button>
          </div>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("brands:list.search")}
            data-testid="brands-search"
            className="h-10 pl-9"
          />
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
          <Switch
            id="brands-show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
            data-testid="brands-show-archived-toggle"
          />
          <Label htmlFor="brands-show-archived" className="cursor-pointer text-xs text-slate-600">
            {t("brands:list.showArchived")}
          </Label>
        </div>
        <span data-testid="brands-count" className="text-xs text-slate-500">
          {t("brands:list.rowsCount", { count: filtered.length })}
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="brands-table-container">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-16 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
          </div>
        ) : filtered.length === 0 ? (
          brands.length === 0 ? (
            <div className="px-8 py-14 text-center" data-testid="brands-empty">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                <Tag className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900">
                {t("brands:list.empty.title")}
              </h3>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
                {t("brands:list.empty.body")}
              </p>
              {canWrite && (
                <div className="mt-6 flex justify-center gap-2">
                  <Button variant="outline" onClick={() => setOpenCreate(true)}
                    className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
                    {t("brands:list.empty.ctaManual")}
                  </Button>
                  <Button onClick={() => setOpenAutofill(true)} className="bg-slate-900 text-white hover:bg-slate-800">
                    <Sparkles className="mr-1 h-4 w-4" strokeWidth={2} />
                    {t("brands:list.empty.ctaUrl")}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="px-6 py-10 text-center text-sm text-slate-500">
              {t("common:search")} — 0
            </div>
          )
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b border-slate-200 bg-slate-50">
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("brands:list.columns.name")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("brands:list.columns.industry")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">
                  <Box className="mr-1 inline h-3 w-3" strokeWidth={2} />{t("brands:list.columns.products")}
                </TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">
                  <Swords className="mr-1 inline h-3 w-3" strokeWidth={2} />{t("brands:list.columns.competitors")}
                </TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("brands:list.columns.updated")}</TableHead>
                <TableHead className="px-6 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500" aria-label="actions"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((b) => {
                const archived = !!b.archived_at;
                return (
                  <TableRow
                    key={b.id}
                    data-testid={`brand-row-${b.id}`}
                    className={`cursor-pointer border-slate-100 transition-colors hover:bg-slate-50 ${archived ? "opacity-60" : ""}`}
                    onClick={() => navigate(`/brands/${b.id}`)}
                  >
                    <TableCell className="px-6 py-4">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900">{b.name}</span>
                          {archived && (
                            <Badge variant="outline" data-testid={`brand-archived-badge-${b.id}`} className="border-amber-200 bg-amber-50 text-[10px] font-semibold text-amber-800">
                              <Archive className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> {t("brands:list.archivedBadge")}
                            </Badge>
                          )}
                        </div>
                        {b.url && (
                          <a href={b.url} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer"
                             className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600">
                            {b.url.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                          </a>
                        )}
                        {b.autofill_source && (
                          <Badge variant="outline" className="mt-1 w-fit border-indigo-200 bg-indigo-50 text-[10px] font-semibold text-indigo-700">
                            <Sparkles className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> AI
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-slate-600">{b.industry || "—"}</TableCell>
                    <TableCell className="px-6 py-4 text-sm text-slate-600">{b.product_count ?? 0}</TableCell>
                    <TableCell className="px-6 py-4 text-sm text-slate-600">{b.competitor_count ?? 0}</TableCell>
                    <TableCell className="px-6 py-4 text-xs text-slate-500">
                      {new Date(b.updated_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="px-2 py-4 text-right">
                      {canWrite && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`brand-row-menu-${b.id}`}
                              className="h-8 px-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                            >
                              <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            {archived ? (
                              <DropdownMenuItem
                                data-testid={`brand-menu-unarchive-${b.id}`}
                                onClick={(e) => { e.stopPropagation(); onUnarchive(b); }}
                              >
                                <ArchiveRestore className="mr-2 h-4 w-4" strokeWidth={1.75} /> {t("brands:detail.unarchiveAction")}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                data-testid={`brand-menu-archive-${b.id}`}
                                onClick={(e) => { e.stopPropagation(); onArchive(b); }}
                              >
                                <Archive className="mr-2 h-4 w-4" strokeWidth={1.75} /> {t("brands:detail.archiveAction")}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <AutofillModal open={openAutofill} onOpenChange={setOpenAutofill} mode="brand" onSuccess={onAutofillSuccess} />
      <CreateBrandModal open={openCreate} onOpenChange={setOpenCreate} onCreated={(b) => navigate(`/brands/${b.id}`)} />
    </div>
  );
}

function CreateBrandModal({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation(["brands", "common", "errors"]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [industry, setIndustry] = useState("");
  const [positioning, setPositioning] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setName(""); setUrl(""); setIndustry(""); setPositioning(""); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/brands", {
        name: name.trim(),
        url: url.trim() || null,
        industry: industry.trim() || null,
        positioning: positioning.trim() || null,
      });
      toast.success(t("brands:detail.saveSuccess"));
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-brand-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-semibold tracking-tight">{t("brands:create.title")}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t("brands:create.subtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.nameLabel")}</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("brands:form.namePlaceholder")} data-testid="create-brand-name" className="h-11" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.urlLabel")}</Label>
              <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("brands:form.urlPlaceholder")} data-testid="create-brand-url" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.industryLabel")}</Label>
              <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder={t("brands:form.industryPlaceholder")} data-testid="create-brand-industry" className="h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.positioningLabel")}</Label>
            <Textarea value={positioning} onChange={(e) => setPositioning(e.target.value)} placeholder={t("brands:form.positioningPlaceholder")} data-testid="create-brand-positioning" rows={3} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="create-brand-cancel">{t("common:cancel")}</Button>
            <Button type="submit" disabled={submitting} data-testid="create-brand-submit" className="bg-slate-900 text-white hover:bg-slate-800">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("brands:create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
